import React from "react";
import styles from "../../pages/renewal/Renew.module.css";
import StatusIndicator from "./StatusIndicator";

/**
 * Vehicle details form section: engine number, color, year, make, model,
 * commercial type (CEI only), and plate number.
 *
 * @param {Object} props
 * @param {Object} props.input - Current form data
 * @param {Function} props.onInputChange - Input change handler
 * @param {Object} props.renewData - Redux renewal data
 * @param {Array} props.vehicleModel - Available vehicle models
 * @param {Array} props.vehicleColor - Available vehicle colors
 * @param {Array} props.ceiMake - CEI makes list
 * @param {Array} props.niconMake - NICON makes list
 * @param {Object} props.yearRange - { start, end, years }
 * @param {boolean} props.makeIdLoading - Whether make ID lookup is loading
 * @param {'success'|'error'|null} props.makeIdStatus - Make ID lookup status
 * @param {boolean} props.modelIdLoading - Whether model ID lookup is loading
 * @param {'success'|'error'|null} props.modelIdStatus - Model ID lookup status
 * @param {string} props.commercialType - Selected commercial type
 * @param {Function} props.onCommercialTypeChange - Commercial type change handler
 */
const VehicleFormFields = ({
  input,
  onInputChange,
  renewData,
  vehicleModel,
  vehicleColor,
  ceiMake,
  niconMake,
  yearRange,
  makeIdLoading,
  makeIdStatus,
  modelIdLoading,
  modelIdStatus,
  commercialType,
  onCommercialTypeChange,
}) => {
  const isNicon = renewData.insuranceType === "NICON";
  const makeArray = isNicon ? niconMake : ceiMake;

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.wrapper_title}>
        Year:{" "}
        {renewData?.getNiconInsuranceRes?.year_of_make ||
          renewData?.getNiconInsuranceRes?.year}{" "}
        - Make:{" "}
        {renewData?.getNiconInsuranceRes?.vehicle_make_custom ||
          renewData?.getNiconInsuranceRes?.make ||
          renewData?.getNiconInsuranceRes?.vehicleMake}{" "}
        - Model:{" "}
        {renewData?.getNiconInsuranceRes?.vehicle_model_custom ||
          renewData?.getNiconInsuranceRes?.model ||
          renewData?.getNiconInsuranceRes?.vehicleModel}{" "}
        - Color:{" "}
        {renewData?.getNiconInsuranceRes?.color || renewData?.vehicleColor}
      </h1>
      <div className={styles.input_container}>
        {/* Engine Number */}
        <input
          className={styles.input_field}
          type="text"
          required
          name="engineNo"
          onChange={onInputChange}
          value={input.engineNo}
          placeholder="Engine Number"
        />

        {/* Color Dropdown */}
        <select
          className={styles.select_field}
          name="color"
          value={input?.color}
          required
          onChange={onInputChange}
        >
          <option value="">Select Color</option>
          {vehicleColor?.map((option) => (
            <option
              key={option.vehicleColorId}
              value={isNicon ? option.colorName : option.vehicleColorId}
            >
              {option.colorName}
            </option>
          ))}
        </select>

        {/* Year Dropdown */}
        <select
          className={styles.input_field}
          name="year"
          required
          onChange={onInputChange}
          value={input.year}
        >
          <option value="">Select Year</option>
          {yearRange.years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        {/* Make Dropdown */}
        <select
          className={isNicon ? styles.input_field : styles.modal_input}
          name="make"
          required
          onChange={onInputChange}
          value={input.make}
        >
          <option value="">
            {isNicon ? "Select Make" : "Select Vehicle Make"}
          </option>

          {isNicon ? (
            <>
              {/* Popular Models Group (Toyota to NISSAN) */}
              <optgroup label="Popular Models">
                {makeArray
                  ?.slice(0, 13) // First 12 items: Toyota to NISSAN
                  .map((make) => (
                    <option key={make.id} value={make.id}>
                      {make.name}
                    </option>
                  ))}
              </optgroup>

              {/* Other Models Group (everything after NISSAN) */}
              <optgroup label="Other Models">
                {makeArray
                  ?.slice(12) // Everything from index 12 onwards
                  .map((make) => (
                    <option key={make.id} value={make.id}>
                      {make.name}
                    </option>
                  ))}
              </optgroup>
            </>
          ) : (
            // CEI makes without grouping
            makeArray?.map((make) => (
              <option key={make.id} value={make.id}>
                {make.name}
              </option>
            ))
          )}
        </select>

        {/* Make ID Status Indicator */}
        <StatusIndicator
          isLoading={makeIdLoading}
          status={makeIdStatus}
          loadingText="Fetching Make ID..."
          successText="Make ID found"
          errorText="Make ID not found"
        />

        {/* Model Dropdown */}
        <select
          className={styles.input_field}
          name="model"
          required
          onChange={onInputChange}
          value={input.model}
        >
          <option value="">Select Model</option>
          {vehicleModel?.map((option) => (
            <option
              key={isNicon ? option.id : option.vehicleModelId}
              value={isNicon ? option.id : option.vehicleModelId}
            >
              {isNicon ? option.name : option.modelName}
            </option>
          ))}
        </select>

        {/* Model ID Status Indicator */}
        <StatusIndicator
          isLoading={modelIdLoading}
          status={modelIdStatus}
          loadingText="Fetching Model ID..."
          successText="Model ID found"
          errorText="Model ID not found"
        />

        {/* Commercial Type (CEI only) */}
        {renewData?.productsDetails?.cei_policy_type === "Commercial" &&
          renewData?.insuranceType === "CEI" && (
            <select
              className={styles.input_field}
              name="commercialType"
              required
              onChange={onCommercialTypeChange}
              value={commercialType}
            >
              <option value="">Select Commercial Type</option>
              <option value="mini bus">Mini Bus</option>
              <option value="mini trucks">Mini Trucks</option>
              <option value="suv">SUV</option>
              <option value="commercial cars">Commercial Cars</option>
              <option value="pickup">Pickup</option>
            </select>
          )}

        {/* Plate Number */}
        <input
          className={styles.input_field}
          type="text"
          placeholder="Plate Number"
          name="plate_no"
          required
          onChange={onInputChange}
          value={input.plate_no}
        />
      </div>
    </div>
  );
};

export default VehicleFormFields;
